import { Box, Typography } from "@mui/material";
import type { GridColDef, GridGroupingColDefOverride } from "@mui/x-data-grid-premium";
import { useGridApiRef, useKeepGroupedColumnsHidden } from "@mui/x-data-grid-premium";
import GridTextHoverLink from "@entrilia/shared/components/grid/GridTextHoverLink";
import DataGrid from "@entrilia/shared/components/grid/DataGrid";
import DataGridGroupHeader from "@entrilia/shared/components/grid/DataGridGroupHeader";
import { useGridGroupsExpanded } from "@entrilia/shared/components/grid/gridHooks";
import { convertISODateTime } from "@entrilia/shared/utilities/dateUtils";
import { stringifyValue } from "@entrilia/shared/utilities/stringHelper";
import { nameof } from "@entrilia/shared/utilities/typeHelper";
import type { WorkflowTemplateVersionInfo } from "../../../../api/types/workflowTypes";

const groupingColumnDefinition: GridGroupingColDefOverride<WorkflowTemplateVersionInfo> = {
  leafField: "name",
  renderHeader: (params) => <DataGridGroupHeader params={params} />,
  flex: 1,
};

const columnDefinitions: GridColDef<WorkflowTemplateVersionInfo>[] = [
  {
    field: nameof<WorkflowTemplateVersionInfo>("category"),
    headerName: "",
    renderCell: ({ rowNode, field, value }) => {
      if (rowNode.type === "group" && field === rowNode.groupingField) {
        return "";
      }

      return (
        <Typography variant="subtitle2" noWrap>
          {stringifyValue(value)}
        </Typography>
      );
    },
  },
  {
    field: nameof<WorkflowTemplateVersionInfo>("name"),
    headerName: "Name",
    flex: 1,
    minWidth: 160,
    renderCell: ({ rowNode, row }) =>
      rowNode.type === "group" ? (
        ""
      ) : (
        <GridTextHoverLink to={`${row.id}`}>
          <Box sx={{ pl: 6, minWidth: 0, width: "100%" }}>
            <Typography noWrap>{row.name}</Typography>
          </Box>
        </GridTextHoverLink>
      ),
  },
  {
    field: nameof<WorkflowTemplateVersionInfo>("createdByUsername"),
    headerName: "Created by",
    flex: 0.5,
    minWidth: 160,
  },
  {
    field: nameof<WorkflowTemplateVersionInfo>("createdAt"),
    headerName: "Created at",
    flex: 0.5,
    minWidth: 160,
    valueFormatter: (value: string) => convertISODateTime(value),
  },
  {
    field: nameof<WorkflowTemplateVersionInfo>("updatedByUsername"),
    headerName: "Modified by",
    flex: 0.5,
    minWidth: 160,
  },
  {
    field: nameof<WorkflowTemplateVersionInfo>("updatedAt"),
    headerName: "Modified at",
    flex: 0.5,
    minWidth: 160,
    valueFormatter: (value: string | undefined) => convertISODateTime(value),
  },
];

interface Props {
  rows: WorkflowTemplateVersionInfo[];
  loading?: boolean;
}

const WorkflowTemplatesGrid = ({ rows, loading }: Props) => {
  const apiRef = useGridApiRef();

  const { isGroupExpanded } = useGridGroupsExpanded(apiRef);

  const gridInitialState = useKeepGroupedColumnsHidden({
    apiRef,
    initialState: {
      rowGrouping: { model: ["category"] },
      columns: {
        columnVisibilityModel: {
          name: false,
        },
      },
    },
  });

  return (
    <DataGrid<WorkflowTemplateVersionInfo>
      apiRef={apiRef}
      rows={rows}
      columns={columnDefinitions}
      groupingColDef={groupingColumnDefinition}
      loading={loading}
      initialState={gridInitialState}
      isGroupExpandedByDefault={isGroupExpanded}
      noRowsText="No templates"
      disableColumnPinning
      disableColumnReorder
      disableColumnSorting
      disableMultipleRowSelection
    />
  );
};

export default WorkflowTemplatesGrid;
